// src/queries/productBulk/useUploadDetails.ts

import { UseQueryOptions, keepPreviousData, useQuery } from '@tanstack/react-query';

import { PageResponse } from '@models/api';
import {
  GetUploadHistoryDetailsParams,
  GetUploadHistoryDetailsResponse,
  getUploadHistoryDetails,
} from '@apis/productBulk/upload';

type UseUploadDetailsArgs = {
  page: number;
  pageSize: number;
  uploadId: number;
  params: GetUploadHistoryDetailsParams;
};

/**
 * Fetch paginated upload history details for Product Bulk Upload.
 *
 * Key points:
 * - queryKey MUST include page/pageSize/filter params to refetch on change.
 * - Never mutate `params` (treat as immutable); build a clean params object for API call.
 * - placeholderData keeps previous page data while fetching next page (better UX).
 */
export const useUploadDetails = ({ page, pageSize, uploadId, params }: UseUploadDetailsArgs) => {
  const onlyFailed = Boolean(params?.onlyFailed);

  const queryOptions: UseQueryOptions<PageResponse<GetUploadHistoryDetailsResponse>, Error> = {
    /**
     * Include ALL variables that affect the result:
     * - uploadId: which job
     * - page/pageSize: pagination
     * - onlyFailed: filter
     */
    queryKey: ['productUploadHistoryDetails', uploadId, page, pageSize, onlyFailed],

    /**
     * Build request params without mutating the input `params`.
     * Send only the expected filter keys to backend.
     */
    queryFn: () => {
      const cleanParams: GetUploadHistoryDetailsParams = {};
      if (onlyFailed) cleanParams.onlyFailed = true;

      return getUploadHistoryDetails(uploadId, {
        pageNo: page,
        pageSize,
        ...cleanParams,
      });
    },

    // Keep previous data visible while fetching next page to reduce flicker.
    placeholderData: keepPreviousData,
  };

  return useQuery(queryOptions);
};
